import React, { useEffect, useState } from "react";
import { Form, Select } from 'antd';
import DepartmentsApi from '../Apis/DepartmentsApi';
import AreasApi from '../Apis/AreasApi';

const DepartmentWithAreaDropdown = ({ form, disabled, initialDepartmentId, initialAreaId }) => {
    const [departments, setDepartments] = useState([]);
    const [allAreas, setAllAreas] = useState([]);
    const [filteredAreas, setFilteredAreas] = useState([]);
    const [deptLoading, setDeptLoading] = useState(false);
    const [areaLoading, setAreaLoading] = useState(false);
    
    // Watch for department changes using Form.useWatch
    const selectedDepartment = Form.useWatch('institutionId', form);

    // Fetch all departments on mount
    useEffect(() => {
        setDeptLoading(true);
        DepartmentsApi.ListDepartments(0, 100)
            .then((response) => {
                setDepartments(response?.payloadDto || []);
                setDeptLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching departments:', err);
                setDeptLoading(false);
            });
    }, []);

    // Fetch all areas on mount (only once)
    useEffect(() => {
        setAreaLoading(true);
        AreasApi.ListAreas(0, 1000)
            .then((response) => {
                setAllAreas(response?.payloadDto || []);
                setAreaLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching areas:', err);
                setAllAreas([]);
                setAreaLoading(false);
            });
    }, []);

    // Set initial department and area values in edit mode
    useEffect(() => {
        if (initialDepartmentId && form) {
            form.setFieldsValue({
                institutionId: initialDepartmentId,
            });
        }
        if (initialAreaId && form) {
            form.setFieldsValue({
                areaId: initialAreaId,
            });
        }
    }, [initialDepartmentId, initialAreaId, form]);

    // Filter areas when department changes
    useEffect(() => {
        if (selectedDepartment) {
            // Filter areas by department ID using institutionDTO?.id
            const filtered = allAreas.filter(area => area?.institutionDTO?.id === selectedDepartment);
            setFilteredAreas(filtered);
        } else if (initialDepartmentId && allAreas.length > 0) {
            // Also filter when initial department is set
            const filtered = allAreas.filter(area => area?.institutionDTO?.id === initialDepartmentId);
            setFilteredAreas(filtered);
        } else {
            setFilteredAreas([]);
        }
    }, [selectedDepartment, allAreas, initialDepartmentId]);

    return (
        <>
            <Form.Item
                name="institutionId"
                label="Department *"
                className="text-lable"
                rules={[{ required: true, message: "Please Select Department." }]}
            >
                <Select
                    disabled={disabled}
                    className="control-Select mb-1"
                    placeholder="Select Department"
                    loading={deptLoading}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {departments.map((dept) => (
                        <Select.Option key={dept.id} value={dept.id}>
                            {dept.institutionName}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="areaId"
                label="Area *"
                className="text-lable"
                rules={[{ required: true, message: "Please Select Area." }]}
            >
                <Select
                    disabled={disabled || (!selectedDepartment && !initialDepartmentId)}
                    className="control-Select mb-1"
                    placeholder={selectedDepartment || initialDepartmentId ? "Select Area" : "Please select department first"}
                    loading={areaLoading}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    notFoundContent={selectedDepartment || initialDepartmentId ? "No areas found for this department" : "Please select a department first"}
                >
                    {filteredAreas.map((area) => (
                        <Select.Option key={area.id} value={area.id}>
                            {area.areaName}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        </>
    );
};

export default DepartmentWithAreaDropdown;